import type {
  AlertSeverity,
  AlertSource,
  AlertStatus,
  NotificationChannel,
} from '@fleetcore/shared/telemetry/alerts'
import {
  AlertOctagon,
  ArrowUpRight,
  Bell,
  Cpu,
  Info,
  Mail,
  MessageSquare,
  Server,
  Smartphone,
  TriangleAlert,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import './i18n'

/**
 * The vocabulary badges.
 *
 * ─── SEVERITY IS COLOUR *AND* SHAPE ─────────────────────────────────────────
 *
 * Each level carries its own icon as well as its own tone. Roughly one man in twelve
 * cannot separate the red from the amber, and this is the screen where that matters:
 * a dispatcher scanning a feed for the one critical row must not be doing it by hue
 * alone. The icon is `aria-hidden` and the text label is always rendered, so nothing
 * here depends on seeing the colour at all.
 */

const SEVERITY_TONE = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
} as const

const SEVERITY_ICON = {
  info: Info,
  warning: TriangleAlert,
  critical: AlertOctagon,
} as const

export function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const { t } = useTranslation()
  const Icon = SEVERITY_ICON[severity]
  return (
    <Badge tone={SEVERITY_TONE[severity]}>
      <Icon aria-hidden className="h-3 w-3" />
      {t(`alerts:severity.${severity}`)}
    </Badge>
  )
}

const STATUS_TONE = {
  open: 'warning',
  acknowledged: 'info',
  resolved: 'success',
} as const

export function StatusBadge({ status }: { status: AlertStatus }) {
  const { t } = useTranslation()
  return <Badge tone={STATUS_TONE[status]}>{t(`alerts:status.${status}`)}</Badge>
}

/**
 * RULE 3, on every row.
 *
 * "The device said so" and "we worked it out" are not the same evidence, and a
 * support conversation about a false positive starts by asking which one it was. The
 * distinction is on the badge and its explanation is in the title attribute rather
 * than buried in a detail panel nobody opens.
 */
export function SourceBadge({ source }: { source: AlertSource }) {
  const { t } = useTranslation()
  const Icon = source === 'device' ? Cpu : Server
  return (
    <span title={t(`alerts:source.${source}Hint`)}>
      <Badge tone="neutral">
        <Icon aria-hidden className="h-3 w-3" />
        {t(`alerts:source.${source}`)}
      </Badge>
    </span>
  )
}

export function EscalatedBadge() {
  const { t } = useTranslation()
  return (
    <Badge tone="accent">
      <ArrowUpRight aria-hidden className="h-3 w-3" />
      {t('alerts:feed.escalatedFlag')}
    </Badge>
  )
}

const CHANNEL_ICON: Record<NotificationChannel, LucideIcon> = {
  in_app: Bell,
  email: Mail,
  sms: MessageSquare,
  // The one channel that reaches a phone with the app closed. Typed as a full Record
  // rather than inferred, so the next channel added to the shared vocabulary fails the
  // build here instead of rendering an undefined component at a dispatcher.
  push: Smartphone,
}

/**
 * RULE 8, rendered honestly.
 *
 * SMS is a stub adapter: it logs what it would have sent and reports a failure. A
 * channel presented as available that silently never delivers is a promise the
 * platform breaks in the one moment it mattered, so the badge is struck through, it
 * says "indisponible" in words, and the reason is on the tooltip.
 */
export function ChannelBadge({ channel }: { channel: NotificationChannel }) {
  const { t } = useTranslation()
  const Icon = CHANNEL_ICON[channel]
  const unavailable = channel === 'sms'

  return (
    <span title={unavailable ? t('alerts:channels.smsHint') : t(`alerts:channels.${channel}Hint`)}>
      <Badge tone={unavailable ? 'neutral' : 'accent'}>
        <Icon aria-hidden className="h-3 w-3" />
        <span className={unavailable ? 'line-through' : undefined}>
          {t(`alerts:channels.${channel}`)}
        </span>
        {unavailable ? (
          <span className="font-semibold text-warning">
            ({t('alerts:channels.smsUnavailable')})
          </span>
        ) : null}
      </Badge>
    </span>
  )
}

/** True for a channel this deployment cannot actually deliver on. */
export function channelIsUnavailable(channel: NotificationChannel): boolean {
  return channel === 'sms'
}
